import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import {
  Div,
  FlexCol,
  InputLabel,
  StyledFormItem,
} from 'common/styles/oc-styled-components';
import { Autocomplete } from 'pages/common/components/Autocomplete/Autocomplete';
import { DIRECTION } from 'pages/common/components/Autocomplete/types';

import { selectDataInsuranceList } from '../../../../connect/selectors';
import {
  DIMENSIONS,
  useWindowDimensions,
} from '../../../../providers/WindowDimensionsProvider';
import { useFunnelStorage } from '../../../funnel/storage/Storage';
import { STEP_KEY } from '../../../funnel/utils';
import { ResponsiveInputRow } from '../../../legacy-checkout/checkout-styled-components';

// Other

import { SelectSkeleton } from './styled-components';

const InsuranceProviderPickerUS = ({
  defaultProvider,
  form,
  updateInsuranceProvider,
  checkRequiredFields,
}) => {
  const [selectedProviderId, setSelectedProviderId] = useState(
    (defaultProvider || {}).id,
  );
  const [isProviderSelected, setIsProviderSelected] = useState(false);

  const insuranceList = useSelector(selectDataInsuranceList);

  const storage = useFunnelStorage();
  const savedInsuranceProgress = storage.get(STEP_KEY.INSURANCE);

  const { layout } = useWindowDimensions();

  // There is a flow that allows users to skip the insurance step in the funnel, which causes
  // our data to not have provider.id. In those cases, we need to use the provider.name to filter
  useEffect(() => {
    if (insuranceList && !selectedProviderId && defaultProvider) {
      setSelectedProviderId(
        insuranceList.find((provider) => provider.name === defaultProvider.name)
          .id,
      );
    }
  }, [insuranceList]);

  useEffect(() => {
    if (!insuranceList) {
      return;
    }

    // Trigger when provider is selected
    if (selectedProviderId && (isProviderSelected || !defaultProvider)) {
      const provider = {
        id: selectedProviderId,
        name: insuranceList.find((p) => p.id === selectedProviderId).name,
      };
      updateInsuranceProvider(provider);
    }

    checkRequiredFields();
  }, [insuranceList, selectedProviderId, isProviderSelected]);

  const handleSelect = (e) => {
    setIsProviderSelected(true);
    setSelectedProviderId(e);
    if (form) {
      form.setFieldsValue({
        insuranceCarrierUS: e,
      });
    }
    checkRequiredFields();
  };

  return (
    <ResponsiveInputRow data-testid="InsuranceProviderPickerUS">
      <FlexCol margin="0 15px 0 0" padding="6px 0 0" width="100%">
        {!insuranceList
        && (
          <StyledFormItem margin={0}>
            <div data-testid="ProviderSkeleton">
              <SelectSkeleton active margin="0 10px 0 0" paragraph={false} />
            </div>
          </StyledFormItem>
        )}
        {insuranceList
        && (
          <StyledFormItem
            margin={0}
            name="insuranceCarrierUS"
            initialValue={selectedProviderId}
            validateTrigger="onSubmit"
          >
            <Div position="relative">
              <Autocomplete
                testid="Provider"
                preferredDropDirection={layout === DIMENSIONS.MOBILE
                  ? DIRECTION.UP
                  : DIRECTION.DOWN}
                onSelect={handleSelect}
                defaultSelected={savedInsuranceProgress.provider || selectedProviderId}
                options={insuranceList
                  .filter((provider) => !provider.isPopular)
                  .sort((a, b) => a.name.localeCompare(b.name))}
                shouldAutoFocus
              />
              <InputLabel>Insurance Provider</InputLabel>
            </Div>
          </StyledFormItem>
        )}
      </FlexCol>
    </ResponsiveInputRow>
  );
};

InsuranceProviderPickerUS.defaultProps = {
  defaultProvider: null,
  checkRequiredFields: () => {},
};

InsuranceProviderPickerUS.propTypes = {
  defaultProvider: PropTypes.object,
  form: PropTypes.object,
  updateInsuranceProvider: PropTypes.func.isRequired,
  checkRequiredFields: PropTypes.func,
};

export default InsuranceProviderPickerUS;
